import styled from '@emotion/styled';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';
import createPersistedState from 'use-persisted-state';
import { v4 } from 'uuid';
import { GAME_MODE, PlayerSetup, SingSetup, SongPreview } from '~/interfaces';
import { Button } from '~/modules/Elements/AKUI/Button';
import { Switcher } from '~/modules/Elements/Switcher';
import { mobileMQ } from '~/modules/Elements/cssMixins';
import events from '~/modules/GameEvents/GameEvents';
import RoomStateStore from '~/modules/Multiplayer/RoomState';
import useKeyboardNav from '~/modules/hooks/useKeyboardNav';
import GameSettings from '~/routes/SingASong/SongSelection/Components/SongSettings/GameSettings';
import MicCheck from '~/routes/SingASong/SongSelection/Components/SongSettings/MicCheck';
import PlayerSettings from '~/routes/SingASong/SongSelection/Components/SongSettings/PlayerSettings';

interface Props {
  songPreview: SongPreview;
  onPlay: (setup: SingSetup & { song: SongPreview }) => void;
  keyboardControl: boolean;
  onExitKeyboardControl: () => void;
}

const difficultyNames = ['Hard', 'Medium', 'Easy'];
const useMpTolerance = createPersistedState<number>('mp_settings-tolerance');

function MultiplayerSongSettings({ songPreview, onPlay, keyboardControl, onExitKeyboardControl }: Props) {
  const [tolerance, setTolerance] = useMpTolerance(1);
  const { register } = useKeyboardNav({
    enabled: keyboardControl,
    onBackspace: onExitKeyboardControl,
  });

  const changeTolerance = () => setTolerance((current) => (current + 1) % difficultyNames.length);

  const handleStart = () => {
    const setup: SingSetup & { song: SongPreview } = {
      id: v4(),
      players: [{ track: 0, number: 0 }],
      mode: GAME_MODE.MULTIPLAYER,
      tolerance: tolerance + 1,
      song: songPreview,
    };
    events.songStarted.dispatch(songPreview, setup);
    onPlay(setup);
  };

  return (
    <Container>
      <GameConfiguration
        initial={{ opacity: 0, translateX: 200 }}
        animate={{ opacity: 1, translateX: 0 }}
        transition={{ duration: 0.2 }}>
        <Switcher
          {...register('mp-difficulty', changeTolerance, 'Change difficulty')}
          label="Difficulty"
          value={difficultyNames[tolerance]}
          className="w-full"
        />
        <Button
          size="large"
          {...register('start-multiplayer', handleStart, undefined, true)}
          className="mobile:px-10 mobile:h-10 mobile:text-md bg-green-700/60 px-20 py-1 text-xl hover:bg-green-600/70">
          Graj!
        </Button>
      </GameConfiguration>
    </Container>
  );
}

export default function SongSettings({ songPreview, onPlay, keyboardControl, onExitKeyboardControl }: Props) {
  const isMultiplayer = RoomStateStore.isInRoom;

  const [singSetup, setSingSetup] = useState<SingSetup | null>(null);
  const [step, setStep] = useState<'song' | 'players'>('song');

  if (isMultiplayer) {
    return (
      <MultiplayerSongSettings
        songPreview={songPreview}
        onPlay={onPlay}
        keyboardControl={keyboardControl}
        onExitKeyboardControl={onExitKeyboardControl}
      />
    );
  }

  const onSongStepFinish = (setup: SingSetup) => {
    setSingSetup(setup);
    setStep('players');
  };
  const startSong = (players: PlayerSetup[]) => {
    if (!singSetup) return;
    const finalSetup = {
      ...singSetup,
      players: players,
    };
    events.songStarted.dispatch(songPreview, finalSetup);
    onPlay({ song: songPreview, ...finalSetup });
  };
  return (
    <Container>
      <MicCheck style={step === 'players' ? undefined : undefined} />
      <AnimatePresence>
        <GameConfiguration
          key={step}
          transition={{
            duration: 0.2,
          }}
          initial={{
            opacity: 0,
            translateX: 200,
          }}
          animate={{
            opacity: 1,
            translateX: 0,
          }}
          exit={{
            opacity: 0,
            translateX: 200,
          }}>
          {step === 'song' && (
            <GameSettings
              songPreview={songPreview}
              onNextStep={onSongStepFinish}
              keyboardControl={keyboardControl}
              onExitKeyboardControl={onExitKeyboardControl}
            />
          )}
          {step === 'players' && (
            <PlayerSettings
              songPreview={songPreview}
              onNextStep={startSong}
              keyboardControl={keyboardControl}
              onExitKeyboardControl={() => setStep('song')}
            />
          )}
        </GameConfiguration>
      </AnimatePresence>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
  width: 100%;

  hr {
    margin: 1rem;
    opacity: 0.25;
  }
  ${mobileMQ} {
    position: fixed;
    bottom: 1rem;
    height: 50vh;
    padding: 0 1rem;
    max-width: 30rem;
    width: 100vw;

    left: auto;
    right: 0;
  }
`;

const GameConfiguration = styled(motion.div)`
  width: 100%;
  max-width: 40%;
  ${mobileMQ} {
    max-width: 100%;
    gap: 0.5rem;
    position: static;
  }
  display: flex;
  flex-direction: column;
  /* align-items: flex-end; */
  gap: 1.25rem;
  position: absolute;
  right: 0;
`;
